import { DatePipe } from "@angular/common";
import { Component } from "@angular/core";
import { CheckGroupPermissions } from "@helpers/checkGroupPermissions";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { LogExport } from "@interfaces/logExport.interface";
import { SalesEnquiry } from "@interfaces/models/salesEnquiry.interface";
import { AppSettingsService } from "@services/appSettings.service";
import { ExportLogService } from "@services/exportLog.service";
import { ProtectedGridService } from "@services/protectedGrid.service";
import { SalesEnquiryService } from "@services/salesEnquiry.service";
import { UserProfileService } from "@services/userProfile.service";
import { ToastService } from "angular-toastify";

@Component({
	moduleId: module.id,
	selector: "salesEnquiries-feasibility",
	styleUrls: ["salesEnquiries-feasibility.css"],
	templateUrl: "salesEnquiries-feasibility.html"
})
export class SalesEnquiriesFeasibilityAdminComponent {
	canExportGrid = false;
	deleteEnabled = true;
	editEnabled = true;
	gridLoading = false;
	headerPrimaryButtons: AdminHeaderButton[] = [];
	headerSecondaryButtons: AdminHeaderButton[] = [];
	isProductionManagement = false;
	isQualityEngineer = false;
	isQualityManagement = false;
	itemType = "Sales Enquiry"
	itemUrl: string = location.href.split("/")[3] + "/" + location.href.split("/")[4];
	items: SalesEnquiry[] = [];
	popupCloseButtonOptions: any = { onClick: () => this.closePopup(), text: "Close" };
	popupSalesEnquiry: SalesEnquiry = new SalesEnquiry();
	popupSaveButtonOptions: any;
	popupTitle = "";
	popupVisible = false;
	title: string;
	users: any[]=[];
	yesNoDropdown: any = [{ id: true, name: "Yes" }, { id: false, name: "No" }];
	
	constructor(private appSettingsService: AppSettingsService, private toastService: ToastService, private exportLogService: ExportLogService, private itemService: SalesEnquiryService, private protectedGridService: ProtectedGridService, private userProfileService: UserProfileService) {
		this.title = "Sales Enquiry Feasibility";
		this.isSignOffAsProductionAvailable = this.isSignOffAsProductionAvailable.bind(this);
		this.isSignOffAsQualityAvailable = this.isSignOffAsQualityAvailable.bind(this);
		this.signOffProductionPopup = this.signOffProductionPopup.bind(this);
		this.signOffQualityPopup = this.signOffQualityPopup.bind(this);
		this.isProductionManagement = CheckGroupPermissions(appSettingsService, "Production Management");
		this.isQualityEngineer = CheckGroupPermissions(appSettingsService, "Quality Engineer");
		this.isQualityManagement = CheckGroupPermissions(appSettingsService, "Quality Management");
		this.getItems();
		this.getUsers();
		this.checkGridExportPermissions();
	}
	
	checkGridExportPermissions() {
		this.protectedGridService.canExport("Sales Enquiry Feasibility")
			.subscribe(
				(res: any) => {
					this.canExportGrid = res.response;
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}
	
	closePopup() {
		this.popupVisible = false;
	}
	
	formatDateTime(dt: Date)
	{
		const datepipe: DatePipe = new DatePipe("en-GB");
		const formattedDate = datepipe.transform(dt, "dd-MMM-YYYY");
		return formattedDate;
	}

	getItems() {
		this.itemService.getRequiringFeasibilitySignOff()
			.subscribe(
				(res: any) => this.items = res.response,
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	getUsers() {
		this.userProfileService.getAllWithUsers()
			.subscribe(
				(res: any) => this.users = res.response,
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	headerButtonClick(method: any) {
		// @ts-ignore // Required to be able to call the method directly from the variable
		if (this[method]) this[method]();
	}

	isSignOffAsProductionAvailable(e: any) {
		return this.isProductionManagement && (e.row.data.productionSignedOffById === undefined || e.row.data.productionSignedOffById === null || e.row.data.productionSignedOffById < 1);
	}

	isSignOffAsQualityAvailable(e: any) {
		return (this.isQualityEngineer === true || this.isQualityManagement === true) && (e.row.data.qualitySignedOffById === undefined || e.row.data.qualitySignedOffById === null || e.row.data.qualitySignedOffById < 1);
	}

	logExport() {
		const logExport = new LogExport();
		logExport.gridName = "Sales Enquiry Feasibility";
		this.exportLogService.logExport(logExport)
			.subscribe(
				() => console.log("Export Logged"),
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
					return false;
				},
			);
	}

	renderUser(id?: number){
		if (id === undefined || id === null) {
			return "";
		}
		
		const element = this.users.find(x => x.id === id);

		return element!.fullName;
	}

	signOffProduction() {
		if (this.isProductionManagement === false) {
			return;
		}
		this.itemService.feasibilitySignOffProduction(this.popupSalesEnquiry.id)
			.subscribe(
				() => {
					this.popupVisible = false;
					this.getItems();
				},
				(err: any) => {
					this.toastService.error("Could not sign off as production");
					console.log(err);
				}
			);
	}

	signOffProductionPopup(e: any) {
		if (this.isProductionManagement === false) {
			return false;
		}
		this.popupSalesEnquiry = e.row.data;
		this.popupTitle = "Production Sign Off";
		this.popupVisible = true;
		this.popupSaveButtonOptions = { onClick: () => this.signOffProduction(), text: "Production Sign Off" };
		return false;
	}

	signOffQuality() {
		if (this.isQualityEngineer === false && this.isQualityManagement === false) {
			return;
		}
		this.itemService.feasibilitySignOffQuality(this.popupSalesEnquiry.id)
			.subscribe(
				() => {
					this.popupVisible = false;
					this.getItems();
				},
				(err: any) => {
					this.toastService.error("Could not sign off as quality");
					console.log(err);
				}
			);
	}

	signOffQualityPopup(e: any) {
		if (this.isQualityEngineer === false && this.isQualityManagement === false) {
			return false;
		}
		this.popupSalesEnquiry = e.row.data;
		this.popupTitle = "Quality Sign Off";
		this.popupVisible = true;
		this.popupSaveButtonOptions = { onClick: () => this.signOffQuality(), text: "Quality Sign Off" };
		return false;
	}

	signedOffYesNo(cellInfo: any){		
		if (cellInfo.value === undefined || cellInfo.value === null || cellInfo.value <= 0)
		{
			return "NO";
		}
		else
		{
			return "YES";
		}
	}
}